/**
 * Auth Manager — secure token management for multi-provider auth.
 *
 * Security:
 * - Access token stored IN MEMORY ONLY (not localStorage — XSS protection)
 * - Refresh token in localStorage (acceptable: it's opaque + rotated)
 * - Auto-refresh on 401 responses
 *
 * Token flow:
 * 1. Login → receive access_token + refresh_token
 * 2. Access token used for API calls (15 min lifetime)
 * 3. On 401 → auto-refresh using refresh_token
 * 4. If refresh fails → redirect to login
 */

// window.__ENV__ нигде не инжектится → база всегда пустая (относительный /api, same-origin).
// Убран мёртвый механизм рантайм-конфига. TODO: при необходимости абсолютной базы — брать из config.js.
const API_BASE = '';

// In-memory storage for access token (XSS-safe: not in localStorage)
let _accessToken = null;
let _refreshPromise = null;

const AuthManager = {
  /**
   * Set tokens after login
   */
  setTokens(accessToken, refreshToken) {
    _accessToken = accessToken;
    if (refreshToken) {
      try {
        localStorage.setItem('refresh_token', refreshToken);
      } catch (e) {
        console.warn('Cannot save refresh token');
      }
    }
  },

  /**
   * Get current access token (from memory)
   */
  getAccessToken() {
    return _accessToken;
  },

  /**
   * Get refresh token (from localStorage)
   */
  getRefreshToken() {
    try {
      return localStorage.getItem('refresh_token');
    } catch (e) {
      return null;
    }
  },

  /**
   * Clear all tokens (logout)
   */
  clearTokens() {
    _accessToken = null;
    try {
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_token'); // Legacy cleanup
    } catch (e) {
      // ignore
    }
  },

  /**
   * Refresh access token using refresh token.
   * Deduplicates concurrent refresh requests.
   */
  async refreshAccessToken() {
    if (_refreshPromise) return _refreshPromise;

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    _refreshPromise = (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!resp.ok) {
          this.clearTokens();
          return null;
        }

        const data = await resp.json();
        this.setTokens(data.access_token, data.refresh_token);
        return data.access_token;
      } catch (e) {
        console.error('Token refresh failed:', e.message);
        this.clearTokens();
        return null;
      } finally {
        _refreshPromise = null;
      }
    })();

    return _refreshPromise;
  },

  /**
   * Make authenticated API request with auto-refresh on 401.
   */
  async fetchWithAuth(url, options = {}) {
    const token = this.getAccessToken();
    const headers = {
      ...options.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    let resp = await fetch(url, { ...options, headers });

    // Auto-refresh on 401
    if (resp.status === 401 && this.getRefreshToken()) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        resp = await fetch(url, { ...options, headers });
      }
    }

    return resp;
  },

  /**
   * Migrate from legacy localStorage token to new system.
   * Call once during app initialization.
   */
  migrateFromLegacy() {
    try {
      const legacyToken = localStorage.getItem('auth_token');
      if (legacyToken && !_accessToken) {
        _accessToken = legacyToken;
        // Don't remove yet — keep for backward compatibility during transition
      }
    } catch (e) {
      // ignore
    }
  },
};

export default AuthManager;
